import { BroadviewClientError, type DataConfidenceMetric } from "@broadview/api-client";
import { PortalAccountMenu } from "@/components/PortalAccountMenu";
import { PortalDataError, PortalLoadingState } from "@/components/PortalDataState";
import { useDataConfidenceQuery } from "@/lib/portal-queries";
import { usePortalRuntime } from "@/lib/portal-runtime";
import { createFileRoute, Link } from "@tanstack/react-router";
import {
  AlertTriangle,
  ArrowLeft,
  CheckCircle2,
  Clock3,
  Database,
  GitBranch,
  ShieldCheck,
} from "lucide-react";

export const Route = createFileRoute("/data-confidence")({
  head: () => ({
    meta: [
      { title: "Data Confidence — Broadview Warranty Intelligence" },
      {
        name: "description",
        content:
          "Tenant-scoped source freshness, completeness, reconciliation and lineage projections with explicit governance.",
      },
    ],
  }),
  component: DataConfidencePage,
});

function tone(metric: DataConfidenceMetric) {
  if (metric.status === "UNAVAILABLE") {
    return "border-border bg-muted text-muted-foreground";
  }
  if (metric.dataState === "SYNTHETIC") {
    return "border-warning/30 bg-warning/10 text-warning";
  }
  if (
    metric.freshness === "CURRENT" &&
    metric.quality === "VERIFIED" &&
    metric.reconciliation === "PASSED"
  ) {
    return "border-success/30 bg-success/10 text-success";
  }
  return "border-warning/30 bg-warning/10 text-warning";
}

const metricIcons = {
  K073: Clock3,
  K074: CheckCircle2,
  K075: Database,
  K076: GitBranch,
} as const;

function DataConfidencePage() {
  const { tenantContext } = usePortalRuntime();
  const confidence = useDataConfidenceQuery();
  const hasPermission =
    tenantContext.permissions.includes("integration.read") &&
    tenantContext.permissions.includes("metric.read");

  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className="sticky top-0 z-30 border-b border-border bg-background/80 backdrop-blur-xl">
        <div className="mx-auto flex max-w-[1400px] items-center gap-4 px-6 py-4">
          <div className="flex items-center gap-2.5">
            <div
              className="flex h-9 w-9 items-center justify-center rounded-lg"
              style={{ background: "var(--gradient-primary)" }}
            >
              <ShieldCheck className="h-5 w-5 text-primary-foreground" />
            </div>
            <div className="leading-tight">
              <div className="text-sm font-semibold">Data Confidence</div>
              <div className="text-xs text-muted-foreground">
                Governed trust evidence · {tenantContext.tenantName}
              </div>
            </div>
          </div>
          <div className="ml-auto flex items-center gap-2">
            <Link
              to="/"
              className="inline-flex items-center gap-1.5 rounded-md border border-border bg-card px-3 py-1.5 text-xs font-medium hover:bg-muted"
            >
              <ArrowLeft className="h-3.5 w-3.5" /> Dashboard
            </Link>
            <PortalAccountMenu />
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-[1400px] space-y-6 px-6 py-8">
        <section className="rounded-xl border border-border bg-card p-5">
          <h1 className="text-xl font-semibold">Can this dashboard evidence be trusted?</h1>
          <p className="mt-2 max-w-4xl text-sm text-muted-foreground">
            These projections read tenant-bound metric snapshots and their source lineage. Missing,
            unapproved, cross-cutoff or incompletely classified facts remain unavailable. The
            browser never substitutes a local fixture after an API failure.
          </p>
        </section>

        {!hasPermission ? (
          <PortalDataError
            title="Data Confidence access is not available"
            error={
              new BroadviewClientError({
                status: 403,
                code: "FORBIDDEN",
                message: "The selected Division must grant integration.read and metric.read.",
              })
            }
            onRetry={() => undefined}
          />
        ) : confidence.isPending ? (
          <PortalLoadingState label="Loading governed Data Confidence" />
        ) : confidence.isError ? (
          <PortalDataError
            title="Data Confidence could not be loaded"
            error={confidence.error}
            onRetry={() => void confidence.refetch()}
          />
        ) : (
          <DataConfidenceContent
            metrics={confidence.data.metrics}
            warnings={confidence.data.warnings}
            dataAsOf={confidence.data.dataAsOf}
          />
        )}
      </main>
    </div>
  );
}

function DataConfidenceContent({
  metrics,
  warnings,
  dataAsOf,
}: {
  metrics: readonly DataConfidenceMetric[];
  warnings: readonly { code: string; message: string; metricIds?: readonly string[] }[];
  dataAsOf: string;
}) {
  return (
    <>
      {warnings.length > 0 && (
        <section className="rounded-xl border border-warning/30 bg-warning/10 p-4">
          <div className="flex items-center gap-2 text-sm font-semibold">
            <AlertTriangle className="h-4 w-4 text-warning" /> Governance notices
          </div>
          <ul className="mt-2 space-y-1 text-xs text-muted-foreground">
            {warnings.map((warning) => (
              <li key={warning.code}>{warning.message}</li>
            ))}
          </ul>
        </section>
      )}

      <section className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
        {metrics.map((metric) => {
          const Icon = metricIcons[metric.metricId];
          return (
            <article key={metric.metricId} className="rounded-xl border border-border bg-card p-5">
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-center gap-2">
                  <Icon className="h-4 w-4 text-primary" />
                  <h2 className="text-sm font-semibold">{metric.label}</h2>
                </div>
                <span className={"rounded-full border px-2 py-0.5 text-[11px] " + tone(metric)}>
                  {metric.dataState}
                </span>
              </div>
              <div className="mt-5 text-3xl font-semibold tabular-nums">
                {metric.status === "AVAILABLE" ? metric.displayValue : "—"}
              </div>
              <div className="mt-3 text-xs text-muted-foreground">
                {metric.status === "UNAVAILABLE"
                  ? metric.unavailableReason
                  : `${metric.quality} quality · ${metric.freshness.toLowerCase()} · ${metric.reconciliation.toLowerCase()} reconciliation`}
              </div>
              {metric.dataState === "SYNTHETIC" && (
                <div className="mt-3 rounded-md border border-warning/30 bg-warning/10 px-2.5 py-2 text-[11px] font-medium text-warning">
                  DEMONSTRATION_ONLY · production use prohibited
                </div>
              )}
            </article>
          );
        })}
      </section>

      <section className="overflow-hidden rounded-xl border border-border bg-card">
        <div className="border-b border-border px-5 py-4">
          <h2 className="text-sm font-semibold">Metric governance and lineage</h2>
          <p className="mt-1 text-xs text-muted-foreground">
            Shared immutable cutoff {new Date(dataAsOf).toLocaleString()}
          </p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted/40 text-[11px] uppercase tracking-wide text-muted-foreground">
              <tr>
                <th className="px-4 py-2.5 text-left">Metric</th>
                <th className="px-4 py-2.5 text-left">Definition</th>
                <th className="px-4 py-2.5 text-left">Source</th>
                <th className="px-4 py-2.5 text-left">Lineage</th>
              </tr>
            </thead>
            <tbody>
              {metrics.map((metric) => (
                <tr key={metric.metricId} className="border-t border-border align-top">
                  <td className="px-4 py-3">
                    <div className="font-medium">{metric.metricId}</div>
                    <div className="mt-1 text-xs text-muted-foreground">{metric.status}</div>
                  </td>
                  <td className="px-4 py-3 text-xs">
                    <div>
                      v{metric.definitionVersion} · {metric.definitionStatus}
                    </div>
                    <div className="mt-1 text-muted-foreground">
                      Formula authority: {metric.formulaAuthority}
                    </div>
                  </td>
                  <td className="max-w-md px-4 py-3 text-xs">
                    <div>{metric.sourceSummary}</div>
                    <div className="mt-1 text-muted-foreground">{metric.authorityClass}</div>
                  </td>
                  <td className="px-4 py-3 text-xs">
                    {metric.provenance.length > 0 ? (
                      <div>
                        {metric.provenance.length} source record
                        {metric.provenance.length === 1 ? "" : "s"}
                        <div className="mt-1 text-muted-foreground">
                          {[...new Set(metric.provenance.map(({ sourceName }) => sourceName))].join(
                            ", ",
                          )}
                        </div>
                      </div>
                    ) : (
                      <span className="text-muted-foreground">No governed lineage available</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </>
  );
}
